import { SubmissionDetailsFieldChange } from '../../../views/shared/components/submission-details-field-changes/submission-details-field-change';
import { PhoneNumberUtils } from '../../../utils/phone-number-utils';
import { Beneficiary } from '../models/beneficiary';
import { BeneficiaryDetailsChanges } from '../models/beneficiary-details-changes';
import * as moment from 'moment';
import { BeneficiaryCardItem } from '../components/beneficiary-card/beneficiary-card-item';
import { BeneficiaryTypeEnum } from '../enums/beneficiary-type.enum';
import { BeneficiarySpouseUtils } from './beneficiary-spouse-utils';
import { BeneficiaryEntityTypeEnum } from '../enums/beneficiary-entity-type.enum';
import { BeneficiaryCardTypeEnum } from '../enums/beneficiary-card-type.enum';
import { AccountTypeBeneficiaryFormGroup } from '../forms/account-type-beneficiary-form-group';
import { Address } from '../../../models/users/dto/address';
import { EstatePrimaryContactTypeEnum } from '../enums/estate-primary-contact-type.enum';
import { BeneficiaryUtils } from './beneficiary-utils';

export class BeneficiarySubmissionUtils {
  /**
   * Formats a SubmissionDetailsFieldChange for display, including localization and specific formatting.
   * @param change - The SubmissionDetailsFieldChange object to format.
   * @return A formatted SubmissionDetailsFieldChange with localized key and formatted value.
   */
  public static formatBeneficiaryDetailsChanges(change: SubmissionDetailsFieldChange): SubmissionDetailsFieldChange {
    let key = '';
    let value = change.value;

    switch (change.key) {
      case 'firstName':
        key = $localize`First Name`;
        break;
      case 'lastName':
        key = $localize`Last Name`;
        break;
      case 'organizationName':
        key = $localize`Organization Name`;
        break;
      case 'organizationPhoneNumber':
        key = $localize`Phone Number`;
        break;
      case 'relationshipToMember':
        key = $localize`Relationship to Member`;
        break;
      case 'birthDate':
        key = $localize`Date of Birth`;
        value = moment(value).format('MMM D, YYYY');
        break;
      case 'socialInsuranceNumber':
        key = $localize`Social Insurance Number`;
        value = value.replace(/\d{3}\d{3}(\d{3})$/, '*** *** $1');
        break;
      case 'email':
        key = $localize`Email Address`;
        break;
      case 'phoneNumber':
        key = $localize`Phone Number`;
        break;
      case 'address.addressLine1':
        key = $localize`Address 1`;
        break;
      case 'address.addressLine2':
        key = $localize`Address 2`;
        break;
      case 'address.addressLine3':
        key = $localize`Address 3`;
        break;
      case 'address.city':
        key = $localize`City/Town`;
        break;
      case 'address.country':
        key = $localize`Country`;
        break;
      case 'address.state':
        key = $localize`Province/State/Region`;
        break;
      case 'address.postalCode':
        key = $localize`Postal Code/ZIP`;
        break;
    }

    return new SubmissionDetailsFieldChange(key, value, change.hasChanges);
  }

  public static toDetailsSection(
    source: SubmissionDetailsFieldChange[],
    keys: string[]
  ): { items: SubmissionDetailsFieldChange[]; notApplicable: boolean } {
    const items = source
      .filter(change => keys.includes(change.key))
      .map(change => this.formatBeneficiaryDetailsChanges(change));

    // A section with no values is Not Applicable even when its fields are flagged as changed —
    // a net-new beneficiary flags every field, which would otherwise render a card of blank rows.
    const hasAnyValue = items.some(item => item.value.trim().length > 0);
    return { items, notApplicable: !hasAnyValue };
  }

  private static getKeysToCompare(entityType: BeneficiaryEntityTypeEnum): string[] {
    switch (entityType) {
      case BeneficiaryEntityTypeEnum.Individual:
        return [
          'relationshipToMember',
          'birthDate',
          'socialInsuranceNumber',
          'email',
          'phoneNumber',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.country',
          'address.postalCode'
        ];
      case BeneficiaryEntityTypeEnum.Organization:
        return [
          'organizationName',
          'organizationPhoneNumber',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.country',
          'address.postalCode',
          'firstName',
          'lastName',
          'phoneNumber'
        ];
      case BeneficiaryEntityTypeEnum.Estate:
        return [
          'firstName',
          'lastName',
          'phoneNumber',
          'organizationName',
          'organizationPhoneNumber',
          'address.addressLine1',
          'address.addressLine2',
          'address.addressLine3',
          'address.city',
          'address.state',
          'address.country',
          'address.postalCode'
        ];
      default:
        return [];
    }
  }

  private static getBeneficiaryIdentityKey(beneficiary: Beneficiary): string {
    if (beneficiary.memberBeneficiaryId !== null) {
      return `mbid:${beneficiary.memberBeneficiaryId}`;
    }

    const address = beneficiary.address;
    const identity = {
      entityTypeId: beneficiary.entityTypeId,
      typeId: beneficiary.typeId,
      firstName: beneficiary.firstName,
      middleName: beneficiary.middleName,
      lastName: beneficiary.lastName,
      socialInsuranceNumber: beneficiary.socialInsuranceNumber,
      email: beneficiary.email,
      phoneNumberCountryCode: beneficiary.phoneNumberCountryCode,
      phoneNumber: beneficiary.phoneNumber,
      birthDate: beneficiary.birthDate,
      relationshipToMemberId: beneficiary.relationshipToMemberId,
      relationshipToMemberOtherDescription: beneficiary.relationshipToMemberOtherDescription,
      organizationName: beneficiary.organizationName,
      organizationPhoneNumberCountryCode: beneficiary.organizationPhoneNumberCountryCode,
      organizationPhoneNumber: beneficiary.organizationPhoneNumber,
      addressLine1: address?.addressLine1 ?? null,
      addressLine2: address?.addressLine2 ?? null,
      addressLine3: address?.addressLine3 ?? null,
      city: address?.city ?? null,
      stateId: address?.stateId ?? null,
      countryId: address?.countryId ?? null,
      postalCode: address?.postalCode ?? null
    };

    return `fields:${JSON.stringify(identity)}`;
  }

  /**
   * Removes duplicate beneficiaries that represent the same person/entity across multiple accounts. A
   * beneficiary applied to several accounts is returned once per account by the backend (each with its own
   * row and address id), which would otherwise render as repeated detail views. Deduping keys on
   * `memberBeneficiaryId` and falls back to identifying fields for net-new beneficiaries.
   * @param beneficiaries - The beneficiaries to dedupe.
   * @returns The beneficiaries with account-level duplicates removed, preserving first-seen order.
   */
  public static dedupeBeneficiaries(beneficiaries: Beneficiary[]): Beneficiary[] {
    const seen = new Set<string>();
    const deduped: Beneficiary[] = [];

    for (const beneficiary of beneficiaries) {
      const key = this.getBeneficiaryIdentityKey(beneficiary);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      deduped.push(beneficiary);
    }

    return deduped;
  }

  public static getBeneficiaryDetailsChanges(
    prevBeneficiaries: Beneficiary[],
    newBeneficiaries: Beneficiary[],
    memberName: string
  ): BeneficiaryDetailsChanges[] {
    const result: BeneficiaryDetailsChanges[] = [];

    // The same beneficiary can appear once per affected account; collapse those to a single detail view.
    for (const newB of this.dedupeBeneficiaries(newBeneficiaries)) {
      // Properties to compare, based on the beneficiary's entity type
      const keys = this.getKeysToCompare(newB.entityTypeId);

      const match = prevBeneficiaries.find(
        prev =>
          prev.memberBeneficiaryId !== null &&
          newB.memberBeneficiaryId !== null &&
          prev.memberBeneficiaryId === newB.memberBeneficiaryId
      );

      const previousChanges: SubmissionDetailsFieldChange[] = [];
      const newChanges: SubmissionDetailsFieldChange[] = [];

      for (const k of keys) {
        const key = k as keyof Beneficiary;
        let hasChanges = false;
        let prevVal: any = '';
        let newVal: any = '';
        let displayPrev: string = '';
        let displayNew: string = '';

        if (match) {
          // Special handling for nested objects and specific formatting
          // i.e: We want to compare country.id but display country.name
          switch (k) {
            case 'relationshipToMember':
              prevVal = match.relationshipToMember?.id;
              newVal = newB.relationshipToMember?.id;
              displayPrev = match.relationshipToMember?.name ?? '';
              displayNew = newB.relationshipToMember?.name ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.addressLine1':
              prevVal = match.address?.addressLine1;
              newVal = newB.address?.addressLine1;
              displayPrev = match.address?.addressLine1 ?? '';
              displayNew = newB.address?.addressLine1 ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.addressLine2':
              prevVal = match.address?.addressLine2;
              newVal = newB.address?.addressLine2;
              displayPrev = match.address?.addressLine2 ?? '';
              displayNew = newB.address?.addressLine2 ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.addressLine3':
              prevVal = match.address?.addressLine3;
              newVal = newB.address?.addressLine3;
              displayPrev = match.address?.addressLine3 ?? '';
              displayNew = newB.address?.addressLine3 ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.city':
              prevVal = match.address?.city;
              newVal = newB.address?.city;
              displayPrev = match.address?.city ?? '';
              displayNew = newB.address?.city ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.country':
              prevVal = match.address?.country?.id;
              newVal = newB.address?.country?.id;
              displayPrev = match.address?.country?.name ?? '';
              displayNew = newB.address?.country?.name ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.state':
              prevVal = match.address?.state?.id;
              newVal = newB.address?.state?.id;
              displayPrev = match.address?.state?.name ?? '';
              displayNew = newB.address?.state?.name ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'address.postalCode':
              prevVal = match.address?.postalCode;
              newVal = newB.address?.postalCode;
              displayPrev = match.address?.postalCode ?? '';
              displayNew = newB.address?.postalCode ?? '';
              hasChanges = prevVal !== newVal;
              break;

            case 'organizationPhoneNumber':
              prevVal = match.organizationPhoneNumber;
              newVal = newB.organizationPhoneNumber;
              hasChanges = prevVal !== newVal;
              displayPrev = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                match.organizationPhoneNumber ?? '',
                match.organizationPhoneNumberCountryCode ?? ''
              );
              displayNew = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                newB.organizationPhoneNumber ?? '',
                newB.organizationPhoneNumberCountryCode ?? ''
              );
              break;

            default:
              prevVal = match[key];
              newVal = newB[key];
              hasChanges = prevVal !== newVal;
              if (k === 'phoneNumber') {
                displayPrev = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                  prevVal,
                  match?.phoneNumberCountryCode ?? ''
                );
                displayNew = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                  newVal,
                  newB?.phoneNumberCountryCode ?? ''
                );
              } else {
                displayPrev = prevVal ?? '';
                displayNew = newVal ?? '';
              }
              break;
          }

          previousChanges.push(new SubmissionDetailsFieldChange(k, displayPrev, hasChanges));
          newChanges.push(new SubmissionDetailsFieldChange(k, displayNew, hasChanges));
        } else {
          // No previous data: treat all as new with hasChanges = true
          switch (k) {
            case 'relationshipToMember':
              newVal = newB.relationshipToMember?.name ?? '';
              break;
            case 'address.addressLine1': {
              newVal = newB.address?.addressLine1 ?? '';
              break;
            }
            case 'address.addressLine2':
              newVal = newB.address?.addressLine2 ?? '';
              break;
            case 'address.addressLine3':
              newVal = newB.address?.addressLine3 ?? '';
              break;
            case 'address.city':
              newVal = newB.address?.city ?? '';
              break;
            case 'address.country':
              newVal = newB.address?.country?.name ?? '';
              break;
            case 'address.state':
              newVal = newB.address?.state?.name ?? '';
              break;
            case 'address.postalCode':
              newVal = newB.address?.postalCode ?? '';
              break;
            case 'phoneNumber':
              newVal = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                newB.phoneNumber ?? '',
                newB.phoneNumberCountryCode ?? ''
              );
              break;
            case 'organizationPhoneNumber':
              newVal = PhoneNumberUtils.formatNumberWithLibPhoneNumber(
                newB.organizationPhoneNumber ?? '',
                newB.organizationPhoneNumberCountryCode ?? ''
              );
              break;
            default:
              newVal = newB[key] ?? '';
              break;
          }

          newChanges.push(new SubmissionDetailsFieldChange(k, newVal, true));
        }
      }

      // Label the beneficiary based on its entity type
      let fullName: string;
      if (newB.entityTypeId === BeneficiaryEntityTypeEnum.Estate) {
        fullName = $localize`Estate of ${memberName}`;
      } else if (newB.entityTypeId === BeneficiaryEntityTypeEnum.Organization) {
        fullName = newB.organizationName ?? '';
      } else {
        fullName = [newB.firstName, newB.middleName, newB.lastName].filter(name => !!name).join(' ');
      }

      result.push({
        fullName,
        previous: previousChanges,
        new: newChanges,
        entityType: newB.entityTypeId
      });
    }

    return result;
  }

  private static beneficiaryCardSortFn(a: BeneficiaryCardItem, b: BeneficiaryCardItem): number {
    const getSortPriority = (beneficiary: Beneficiary): number => {
      if (BeneficiarySpouseUtils.isSpouseBeneficiaryType(beneficiary.typeId)) return 0;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Individual) return 1;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Organization) return 2;
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Estate) return 3;
      return 4;
    };

    const getSortKey = (beneficiary: Beneficiary): string => {
      if (beneficiary.entityTypeId === BeneficiaryEntityTypeEnum.Individual) {
        return [beneficiary.firstName, beneficiary.middleName, beneficiary.lastName]
          .filter(name => !!name)
          .join(' ')
          .toLowerCase();
      } else {
        return (beneficiary.organizationName ?? '').toLowerCase();
      }
    };

    const priorityA = getSortPriority(a);
    const priorityB = getSortPriority(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const keyA = getSortKey(a);
    const keyB = getSortKey(b);

    return keyA.localeCompare(keyB);
  }

  public static shareChangesEqual(
    prevBeneficiary: Beneficiary,
    newBeneficiary: Beneficiary,
    type: BeneficiaryTypeEnum
  ): boolean {
    if (prevBeneficiary.typeId !== newBeneficiary.typeId) {
      return false;
    }
    if (type === BeneficiaryTypeEnum.Contingent) {
      return prevBeneficiary.contingentSharePercentage === newBeneficiary.contingentSharePercentage;
    } else {
      return prevBeneficiary.sharePercentage === newBeneficiary.sharePercentage;
    }
  }

  public static getBeneficiaryCardItems(
    source: Beneficiary[],
    target: Beneficiary[],
    sourceCardType: BeneficiaryCardTypeEnum
  ): BeneficiaryCardItem[] {
    return source
      .map(beneficiary => {
        const match = target.find(b => b.memberBeneficiaryId === beneficiary.memberBeneficiaryId);
        const isChanged = !match || !this.shareChangesEqual(beneficiary, match, beneficiary.typeId);

        const cardType = isChanged ? sourceCardType : BeneficiaryCardTypeEnum.Neutral;
        return { ...beneficiary, cardType } as BeneficiaryCardItem;
      })
      .sort(this.beneficiaryCardSortFn);
  }

  private static constructIndividualBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const personalInfoGroup = formGroup.individual?.controls?.personalInfo;
    const contactInfoGroup = formGroup.individual?.controls?.contactInfo;

    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;
    beneficiary.relationshipToMemberId = personalInfoGroup?.controls?.relationshipToMemberId?.value ?? 0;
    beneficiary.relationshipToMemberOtherDescription =
      personalInfoGroup?.controls?.relationshipToMemberOtherDescription?.value ?? null;
    beneficiary.firstName = personalInfoGroup?.controls?.firstName?.value ?? null;
    beneficiary.middleName = personalInfoGroup?.controls?.middleName?.value ?? null;
    beneficiary.lastName = personalInfoGroup?.controls?.lastName?.value ?? null;
    beneficiary.socialInsuranceNumber = personalInfoGroup?.controls?.socialInsuranceNumber?.value ?? '';
    beneficiary.email = contactInfoGroup?.controls?.email?.value ?? '';
    beneficiary.phoneNumberCountryCode = contactInfoGroup?.controls?.phoneNumberCountryCode?.value ?? '';
    beneficiary.phoneNumber = contactInfoGroup?.controls?.phoneNumber?.value ?? '';
    beneficiary.birthDate = personalInfoGroup?.controls?.birthDate?.value ?? '';
    beneficiary.address = Deserialize?.instanceOf(Address, contactInfoGroup?.controls?.address?.value) ?? null;
    if (formGroup.individual?.controls?.hasTrustee?.value) {
      beneficiary.trustee = BeneficiaryUtils.createTrusteeReqFromFormValue(
        formGroup?.individual?.controls?.trustee?.value
      );
    }

    return beneficiary;
  }

  private static constructOrganizationBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const organizationControls = formGroup.organization?.controls;
    const primaryContactControls = organizationControls?.primaryContact?.controls;
    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;
    beneficiary.firstName = primaryContactControls?.firstName?.value ?? null;
    beneficiary.lastName = primaryContactControls?.lastName?.value ?? null;
    beneficiary.organizationName = organizationControls?.organizationName?.value ?? null;
    beneficiary.organizationPhoneNumberCountryCode =
      organizationControls?.organizationPhoneNumberCountryCode?.value ?? '';
    beneficiary.organizationPhoneNumber = organizationControls?.organizationPhoneNumber?.value ?? '';
    beneficiary.phoneNumberCountryCode = primaryContactControls?.phoneNumberCountryCode?.value ?? '';
    beneficiary.phoneNumber = primaryContactControls?.phoneNumber?.value ?? '';
    beneficiary.address = Deserialize?.instanceOf(Address, organizationControls?.address?.value) ?? null;
    return beneficiary;
  }

  private static constructEstateBeneficiary(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const Deserialize = window?.injector.Deserialize;
    const beneficiary = new Beneficiary();
    const estateControls = formGroup.estate?.controls;
    const contactType = estateControls?.primaryContactType?.value;
    const organizationControls = estateControls?.organization?.controls;
    const hasOrganizationContact = !!organizationControls?.organizationName?.value;
    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;

    if (contactType === EstatePrimaryContactTypeEnum.Organization || hasOrganizationContact) {
      beneficiary.organizationName = organizationControls?.organizationName?.value ?? null;
      beneficiary.organizationPhoneNumberCountryCode =
        organizationControls?.organizationPhoneNumberCountryCode?.value ?? '';
      beneficiary.organizationPhoneNumber = organizationControls?.organizationPhoneNumber?.value ?? '';
      beneficiary.address = Deserialize?.instanceOf(Address, organizationControls?.address?.value) ?? null;
    } else if (contactType === EstatePrimaryContactTypeEnum.Individual) {
      const individualControls = estateControls?.individual?.controls;
      beneficiary.firstName = individualControls?.firstName?.value ?? null;
      beneficiary.lastName = individualControls?.lastName?.value ?? null;
      beneficiary.phoneNumberCountryCode = individualControls?.phoneNumberCountryCode?.value ?? '';
      beneficiary.phoneNumber = individualControls?.phoneNumber?.value ?? '';
    }
    return beneficiary;
  }

  public static createBeneficiaryFromFormGroup(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    let beneficiary!: Beneficiary;
    switch (formGroup.entityTypeId.value) {
      case BeneficiaryEntityTypeEnum.Individual:
        beneficiary = this.constructIndividualBeneficiary(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Organization:
        beneficiary = this.constructOrganizationBeneficiary(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Estate:
        beneficiary = this.constructEstateBeneficiary(formGroup);
        break;
    }

    if (beneficiary.typeId === BeneficiaryTypeEnum.Contingent) {
      beneficiary.contingentSharePercentage = formGroup.contingentSharePercentage?.value ?? 0;
    } else {
      beneficiary.sharePercentage = formGroup.sharePercentage?.value ?? 0;
    }

    return beneficiary;
  }
}
